/**
 * Pure helpers for the Reanalysis tab's batch history (project_management#550).
 *
 * Kept out of the component so the paging and URL-state rules are unit-tested
 * without rendering a DataGrid: the bug this ticket fixes was exactly these
 * rules being absent (fixed page sizes, no offsets, no totals).
 *
 * #574: a batch may span several patients (`targetUserId` null,
 * `targetUserIds` lists them); items carry their patient; a finished batch's
 * failed items can be retried as a new batch.
 */

export const REANALYSIS_TAB = 'reanalysis';
export const BATCH_PAGE_SIZES = [10, 25, 50, 100];
export const ITEM_PAGE_SIZES = [25, 50, 100];

/** Query-string keys, so a filtered batch view can be shared and reopened. */
export const URL_KEYS = { tab: 'tab', patient: 'patient', batch: 'batch' } as const;

export interface HistoryUrlState {
  /** Applied patient (userId) filter; '' = all patients. */
  patient: string;
  /** Batch shown in the detail panel; null = auto (live batch / newest match). */
  batchId: string | null;
}

export function readHistoryUrlState(params: URLSearchParams): HistoryUrlState {
  return {
    patient: (params.get(URL_KEYS.patient) ?? '').trim(),
    batchId: params.get(URL_KEYS.batch) || null,
  };
}

/** Returns new params; also pins the admin dashboard to the Reanalysis tab. */
export function writeHistoryUrlState(
  params: URLSearchParams,
  state: HistoryUrlState,
): URLSearchParams {
  const next = new URLSearchParams(params);

  next.set(URL_KEYS.tab, REANALYSIS_TAB);
  if (state.patient) next.set(URL_KEYS.patient, state.patient);
  else next.delete(URL_KEYS.patient);
  if (state.batchId) next.set(URL_KEYS.batch, state.batchId);
  else next.delete(URL_KEYS.batch);

  return next;
}

/** Server paging: the grid's zero-based page → the API's limit/offset. */
export const pageVariables = (page: number, pageSize: number) => ({
  limit: pageSize,
  offset: Math.max(0, page) * pageSize,
});

interface Counts {
  total: number;
  pending?: number;
  inFlight?: number;
  succeeded: number;
  failed: number;
  skipped: number;
}

export interface BatchSummary {
  id: string;
  scope: string;
  status: string;
  /** Null for a batch that spans several patients (#574). */
  targetUserId: string | null;
  targetUserIds?: string[] | null;
  patientCount?: number | null;
  multiPatient?: boolean | null;
  retryOf?: string | null;
  reason: string;
  createdAt: number;
  counts: Counts;
}

export interface BatchItem {
  id: string;
  targetId: string;
  /** The target's owner (#574). */
  userId?: string | null;
  fileName?: string | null;
  state: string;
  attempts: number;
  durationMs: number | null;
  completedAt: number | null;
  error: string | null;
}

export const LIVE_STATUSES = ['queued', 'running', 'paused'];

/** "patient-id" for a single-patient batch, "3 patients" for a multi-patient one. */
export function patientLabel(
  batch: Pick<BatchSummary, 'targetUserId' | 'targetUserIds' | 'patientCount'>,
): string {
  if (batch.targetUserId) return batch.targetUserId;
  const count = batch.patientCount ?? batch.targetUserIds?.length ?? 0;

  return `${count} patient${count === 1 ? '' : 's'}`;
}

/** The patient filter applies only to batches with more than one patient. */
export function itemPatientOptions(
  batch: Pick<BatchSummary, 'targetUserIds' | 'multiPatient'> | undefined,
): string[] {
  return batch?.multiPatient ? batch.targetUserIds ?? [] : [];
}

/** "Retry failed" (#574): a finished batch with failures. */
export function canRetryFailed(batch: Pick<BatchSummary, 'status' | 'counts'> | undefined) {
  return Boolean(batch && !LIVE_STATUSES.includes(batch.status) && batch.counts.failed > 0);
}

/** The reason the retry batch will carry — mirrors the backend's `retryReason`. */
export const retryReasonOf = (reason: string) =>
  reason.endsWith(' (retry)') ? reason : `${reason} (retry)`;

/**
 * Which batch the detail panel shows: an explicit pick wins, then a live batch
 * (the operator is watching it run), then — when searching one patient — their
 * newest batch, so a search lands directly on something with a failures button.
 */
export function focusBatchId(args: {
  selectedBatchId: string | null;
  batches: BatchSummary[];
  patient: string;
}): string | null {
  if (args.selectedBatchId) return args.selectedBatchId;
  const live = args.batches.find((batch) => LIVE_STATUSES.includes(batch.status));

  if (live) return live.id;

  return args.patient ? args.batches[0]?.id ?? null : null;
}

/**
 * Total to page the items grid against. `itemsTotal` is the server's exact
 * count for the current item filter; the cached batch counts are the fallback
 * for a backend that predates it.
 */
export function itemRowCount(
  batch: { itemsTotal?: number | null; counts?: Counts } | undefined,
  failuresOnly: boolean,
): number {
  if (!batch) return 0;
  if (typeof batch.itemsTotal === 'number') return batch.itemsTotal;

  return (failuresOnly ? batch.counts?.failed : batch.counts?.total) ?? 0;
}

const isoOrBlank = (value?: number | null) => (value ? new Date(value).toISOString() : '');

export function toBatchRows(batches: BatchSummary[]) {
  return batches.map((b) => ({
    id: b.id,
    scope: b.scope,
    status: b.status,
    patient: patientLabel(b),
    reason: b.reason,
    progress: `${b.counts.succeeded + b.counts.failed + b.counts.skipped}/${b.counts.total}`,
    failed: b.counts.failed,
    createdAt: isoOrBlank(b.createdAt),
  }));
}

export function toItemRows(items: BatchItem[]) {
  return items.map((item) => ({
    id: item.id,
    targetId: item.targetId,
    userId: item.userId ?? '',
    fileName: item.fileName ?? '',
    state: item.state,
    attempts: item.attempts,
    durationMs: item.durationMs ?? '',
    completedAt: isoOrBlank(item.completedAt),
    error: item.error ?? '',
  }));
}
